<template>
  <div class="ForWhomSelectorComponent">
    <BaseSelect
      id="forwho"
      :label="t(props.forWhoLabel)"
      :items="forWhomOptions"
      v-model="innerForWhom"
    />

    <BaseSelect
      v-if="innerForWhom !== ForWhomEnum.ME"
      id="people"
      :label="t('rater.common.for-whom.how-many')"
      :items="forWhomPeopleOptions"
      v-model="innerForWhomPeople"
    />
  </div>
</template>

<script setup lang="ts">
  import { ForWhomEnum } from '@/modules/core/models/enums'
  import { arrayRange } from '@/utils/array.utils'
  import { computed, watch } from 'vue'

  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { useI18n } from 'vue-i18n'

  const MIN_CUSTOM_PEOPLE = 1
  const MAX_CUSTOM_PEOPLE = 10

  const { t } = useI18n()

  interface PropsInterface {
    forWhom: string | null
    forWhomPeople: number
    forWhoLabel?: string
    maxPeople?: number
    isPymes?: boolean
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    forWhom: ForWhomEnum.ME,
    forWhomPeople: 1,
    forWhoLabel: 'rater.common.for-whom.form-legend',
    isPymes: false
  })

  const emit = defineEmits(['update:forWhom', 'update:forWhomPeople'])

  const forWhomOptions: OptionsValuesInterface[] = [
    ...(props.isPymes
      ? []
      : [
          {
            label: t('rater.common.for-whom.options.me'),
            value: ForWhomEnum.ME
          }
        ]),

    {
      label: t('rater.common.for-whom.options.me-more'),
      value: ForWhomEnum.ME_MORE
    },
    {
      label: t('rater.common.for-whom.options.more'),
      value: ForWhomEnum.NOT_ME
    }
  ]
  const maxPeople = props.maxPeople || MAX_CUSTOM_PEOPLE
  const peopleRange = arrayRange(MIN_CUSTOM_PEOPLE, maxPeople)
  const peopleOptionsRange = peopleRange.map((_, i: number) => ({
    label: formatPeopleOption(1 + i),
    value: String(1 + i)
  }))

  const forWhomPeopleOptions = computed(() => {
    if (innerForWhom.value === ForWhomEnum.ME_MORE || props.isPymes) {
      return peopleOptionsRange.slice(1)
    }

    if (innerForWhom.value === ForWhomEnum.NOT_ME) {
      return peopleOptionsRange
    }

    return []
  })

  const innerForWhom = computed({
    get: () => props.forWhom,
    set: (value: string | null) => emit('update:forWhom', value)
  })

  const innerForWhomPeople = computed({
    get: () => String(props.forWhomPeople),
    set: (value: string) => emit('update:forWhomPeople', Number(value))
  })

  watch(forWhomPeopleOptions, () => {
    if (innerForWhom.value === ForWhomEnum.ME_MORE || props.isPymes) {
      innerForWhomPeople.value = String(MIN_CUSTOM_PEOPLE + 1)
      return
    }

    innerForWhomPeople.value = String(MIN_CUSTOM_PEOPLE)
  })

  function formatPeopleOption(items: number) {
    return t('components.InsuredSubheaderComponent.label', items)
  }
</script>
